import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/auth'
import {
  Music,
  Settings,
  Play,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  Download,
  Link,
  Server,
  HardDrive,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  XCircle,
  SkipForward,
  Pause,
  RotateCcw,
  Square,
  FileText,
  Info,
  Upload,
  FolderSearch,
  FolderSync,
  ListMusic,
  LogOut,
  Heart,
  ExternalLink,
  Youtube,
} from 'lucide-react'
import {
  loadSpotifyAccessToken,
  clearSpotifyAuth,
  initiateSpotifyAuth,
  getSpotifyUser,
  getAllSpotifyPlaylists,
  getAllPlaylistTracks,
  getAllLikedSongs,
  getSpotifyClientId,
  type SpotifyPlaylist,
  type SpotifyUser,
} from '../utils/spotifyAuth'
import { downloadSettings, importer } from '../api/client'
import type {
  UserDownloadSettings,
  ImportJobWithDetails,
  ImportJobAlbum,
  ProwlarrSearchResult,
  DabSearchResult,
  YtSearchResult,
} from '../api/client'

type TabType = 'connect' | 'import' | 'jobs' | 'settings'

const statusColors: Record<string, string> = {
  pending: 'text-dark-400',
  parsing: 'text-yellow-400',
  searching: 'text-yellow-400',
  found: 'text-blue-400',
  not_found: 'text-orange-400',
  selected: 'text-blue-400',
  downloading: 'text-blue-400',
  dab_downloading: 'text-purple-400',
  slskd_downloading: 'text-green-400',
  yt_downloading: 'text-red-400',
  downloaded: 'text-cyan-400',
  syncing: 'text-purple-400',
  synced: 'text-green-400',
  organizing: 'text-purple-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
  skipped: 'text-dark-500',
  paused: 'text-yellow-400',
  cancelled: 'text-dark-500',
  stopped: 'text-orange-400',
  error: 'text-red-400',
}

const logLevelColors: Record<string, string> = {
  info: 'text-blue-400',
  warn: 'text-yellow-400',
  error: 'text-red-400',
  debug: 'text-dark-400',
}

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock size={16} />,
  parsing: <RefreshCw size={16} className="animate-spin" />,
  searching: <RefreshCw size={16} className="animate-spin" />,
  found: <Search size={16} />,
  not_found: <AlertCircle size={16} />,
  selected: <Check size={16} />,
  downloading: <Download size={16} className="animate-pulse" />,
  dab_downloading: <Music size={16} className="animate-pulse" />,
  slskd_downloading: <Download size={16} className="animate-pulse" />,
  yt_downloading: <Youtube size={16} className="animate-pulse" />,
  downloaded: <Check size={16} />,
  syncing: <RefreshCw size={16} className="animate-spin" />,
  synced: <CheckCircle size={16} />,
  organizing: <HardDrive size={16} />,
  completed: <CheckCircle size={16} />,
  failed: <XCircle size={16} />,
  skipped: <SkipForward size={16} />,
  paused: <Pause size={16} />,
  cancelled: <Square size={16} />,
  stopped: <Square size={16} />,
  error: <XCircle size={16} />,
}

export default function SpotifyImporter() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const isAdmin = user?.is_admin ?? false
  const [activeTab, setActiveTab] = useState<TabType>('connect')
  const [playlistUrl, setPlaylistUrl] = useState('')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [expandedJob, setExpandedJob] = useState<string | null>(null)
  const [searchingAlbum, setSearchingAlbum] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<ProwlarrSearchResult[]>([])
  const [searchingDabAlbum, setSearchingDabAlbum] = useState<string | null>(null)
  const [dabSearchResults, setDabSearchResults] = useState<DabSearchResult[]>([])
  const [dabManualSearchAlbum, setDabManualSearchAlbum] = useState<string | null>(null) // Which album has manual search open
  const [dabSearchQuery, setDabSearchQuery] = useState('') // Custom search query
  const [showLogsFor, setShowLogsFor] = useState<string | null>(null)
  // YouTube search state
  const [searchingYoutubeAlbum, setSearchingYoutubeAlbum] = useState<string | null>(null)
  const [youtubeSearchResults, setYoutubeSearchResults] = useState<YtSearchResult[]>([])
  const [youtubeSearchQuery, setYoutubeSearchQuery] = useState('')
  const [downloadingYoutube, setDownloadingYoutube] = useState<string | null>(null) // Track ID being downloaded

  // Spotify connection state
  const [spotifyToken, setSpotifyToken] = useState<string | null>(null)
  const [spotifyUser, setSpotifyUser] = useState<SpotifyUser | null>(null)
  const [spotifyPlaylists, setSpotifyPlaylists] = useState<SpotifyPlaylist[]>([])
  const [selectedPlaylists, setSelectedPlaylists] = useState<Set<string>>(new Set())
  const [includeLikedSongs, setIncludeLikedSongs] = useState(false)
  const [likedSongsCount, setLikedSongsCount] = useState<number | null>(null)
  const [spotifyLoading, setSpotifyLoading] = useState(false)
  const [spotifyError, setSpotifyError] = useState<string | null>(null)
  const [spotifyClientId, setSpotifyClientIdInput] = useState('')
  const [importingPlaylists, setImportingPlaylists] = useState(false)
  const [importProgress, setImportProgress] = useState<{ current: number; total: number; name: string } | null>(null)

  // Settings state
  const [settings, setSettings] = useState<Partial<UserDownloadSettings>>({})
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  // Fetch download settings to get Spotify client ID
  const { data: downloadSettingsData } = useQuery({
    queryKey: ['downloadSettings'],
    queryFn: () => downloadSettings.get(),
  })

  // Get the configured client ID from server settings
  const configuredClientId = downloadSettingsData?.spotify_client_id

  // Check for existing Spotify token on mount
  useEffect(() => {
    const token = loadSpotifyAccessToken()
    if (token) {
      setSpotifyToken(token)
      loadSpotifyData(token)
    }
    // Load saved client ID (fallback for manual entry)
    const savedClientId = getSpotifyClientId()
    if (savedClientId) {
      setSpotifyClientIdInput(savedClientId)
    }
  }, [])

  // Auto-set client ID from server settings
  useEffect(() => {
    if (configuredClientId && !spotifyClientId) {
      setSpotifyClientIdInput(configuredClientId)
    }
  }, [configuredClientId])

  // Load Spotify user and playlists
  const loadSpotifyData = async (token: string) => {
    setSpotifyLoading(true)
    setSpotifyError(null)
    try {
      const [userData, playlistsData] = await Promise.all([
        getSpotifyUser(token),
        getAllSpotifyPlaylists(token),
      ])
      setSpotifyUser(userData)
      setSpotifyPlaylists(playlistsData)

      // Get liked songs count
      const response = await fetch('https://api.spotify.com/v1/me/tracks?limit=1', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setLikedSongsCount(data.total)
      }
    } catch (err) {
      setSpotifyError(err instanceof Error ? err.message : 'Failed to load Spotify data')
      if (err instanceof Error && err.message.includes('expired')) {
        setSpotifyToken(null)
      }
    } finally {
      setSpotifyLoading(false)
    }
  }

  // Handle Spotify connect
  const handleSpotifyConnect = async () => {
    // Use configured client ID from server if available, otherwise use local input
    const clientIdToUse = configuredClientId || spotifyClientId.trim()
    if (!clientIdToUse) {
      setSpotifyError('Please enter your Spotify Client ID')
      return
    }
    try {
      await initiateSpotifyAuth(clientIdToUse)
    } catch (err) {
      setSpotifyError(err instanceof Error ? err.message : 'Failed to initiate Spotify auth')
    }
  }

  // Handle Spotify disconnect
  const handleSpotifyDisconnect = () => {
    clearSpotifyAuth()
    setSpotifyToken(null)
    setSpotifyUser(null)
    setSpotifyPlaylists([])
    setSelectedPlaylists(new Set())
    setIncludeLikedSongs(false)
  }

  // Toggle playlist selection
  const togglePlaylistSelection = (id: string) => {
    const newSelected = new Set(selectedPlaylists)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedPlaylists(newSelected)
  }

  // Select all playlists
  const selectAllPlaylists = () => {
    setSelectedPlaylists(new Set(spotifyPlaylists.map((p) => p.id)))
    setIncludeLikedSongs(true)
  }

  // Deselect all
  const deselectAllPlaylists = () => {
    setSelectedPlaylists(new Set())
    setIncludeLikedSongs(false)
  }

  // Import selected playlists
  const handleImportSelected = async () => {
    if (!spotifyToken) return
    if (selectedPlaylists.size === 0 && !includeLikedSongs) {
      setSpotifyError('Please select at least one playlist or include liked songs')
      return
    }

    setImportingPlaylists(true)
    setSpotifyError(null)

    const total = selectedPlaylists.size + (includeLikedSongs ? 1 : 0)
    let current = 0

    try {
      // Import liked songs first if selected
      if (includeLikedSongs) {
        setImportProgress({ current: 1, total, name: 'Liked Songs' })
        const tracks = await getAllLikedSongs(spotifyToken)
        const validTracks = tracks
          .filter((t) => t.track)
          .map((t) => ({
            artist_name: t.track!.artists.map((a) => a.name).join(', '),
            track_name: t.track!.name,
            album_name: t.track!.album.name,
          }))

        if (validTracks.length > 0) {
          await importer.importTracks('Liked Songs', validTracks, true)
        }
        current++
      }

      // Import each selected playlist
      for (const playlistId of selectedPlaylists) {
        const playlist = spotifyPlaylists.find((p) => p.id === playlistId)
        if (!playlist) continue

        current++
        setImportProgress({ current, total, name: playlist.name })

        const tracks = await getAllPlaylistTracks(spotifyToken, playlistId)
        const validTracks = tracks
          .filter((t) => t.track)
          .map((t) => ({
            artist_name: t.track!.artists.map((a) => a.name).join(', '),
            track_name: t.track!.name,
            album_name: t.track!.album.name,
          }))

        if (validTracks.length > 0) {
          await importer.importTracks(playlist.name, validTracks, false)
        }
      }

      // Success - refresh jobs and go to jobs tab
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setSelectedPlaylists(new Set())
      setIncludeLikedSongs(false)
      setActiveTab('jobs')
    } catch (err) {
      setSpotifyError(err instanceof Error ? err.message : 'Failed to import playlists')
    } finally {
      setImportingPlaylists(false)
      setImportProgress(null)
    }
  }


  // Fetch settings
  const { data: currentSettings } = useQuery({
    queryKey: ['downloadSettings'],
    queryFn: downloadSettings.get,
  })

  // Update local settings state when fetched
  if (currentSettings && !settingsLoaded) {
    setSettings(currentSettings)
    setSettingsLoaded(true)
  }

  // Fetch import jobs
  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['importJobs'],
    queryFn: importer.listJobs,
    refetchInterval: 5000, // Poll every 5 seconds
  })

  // Mutations
  const saveSettingsMutation = useMutation({
    mutationFn: () => downloadSettings.update(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloadSettings'] })
    },
  })

  const testConnectionMutation = useMutation({
    mutationFn: downloadSettings.test,
  })

  const importMutation = useMutation({
    mutationFn: (url: string) => importer.importSpotify(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setPlaylistUrl('')
      setActiveTab('jobs')
    },
  })

  const importCsvMutation = useMutation({
    mutationFn: (file: File) => importer.importCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setCsvFile(null)
      setActiveTab('jobs')
    },
  })

  const deleteJobMutation = useMutation({
    mutationFn: importer.deleteJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const searchAlbumMutation = useMutation({
    mutationFn: ({ jobId, albumId }: { jobId: string; albumId: string }) =>
      importer.searchAlbum(jobId, albumId),
    onSuccess: (results) => {
      setSearchResults(results)
    },
  })

  const selectResultMutation = useMutation({
    mutationFn: ({
      jobId,
      albumId,
      guid,
      indexerId,
    }: {
      jobId: string
      albumId: string
      guid: string
      indexerId: number
    }) => importer.selectResult(jobId, albumId, guid, indexerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setSearchingAlbum(null)
      setSearchResults([])
    },
  })

  // DAB search mutation
  const searchAlbumDabMutation = useMutation({
    mutationFn: ({ jobId, albumId, query }: { jobId: string; albumId: string; query?: string }) =>
      importer.searchAlbumDab(jobId, albumId, query),
    onSuccess: (results) => {
      setDabSearchResults(results)
    },
  })

  // DAB select mutation
  const selectDabResultMutation = useMutation({
    mutationFn: ({
      jobId,
      albumId,
      dabAlbumId,
    }: {
      jobId: string
      albumId: string
      dabAlbumId: string
    }) => importer.selectDabResult(jobId, albumId, dabAlbumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setSearchingDabAlbum(null)
      setDabSearchResults([])
    },
  })

  // YouTube search mutation
  const searchYoutubeMutation = useMutation({
    mutationFn: ({ query }: { query: string }) => importer.searchYoutube(query, 5),
    onSuccess: (results) => {
      setYoutubeSearchResults(results)
    },
  })

  // YouTube download mutation
  const downloadYoutubeMutation = useMutation({
    mutationFn: ({
      jobId,
      albumId,
      url,
      artist,
      album,
      title,
      trackNumber,
    }: {
      jobId: string
      albumId: string
      url: string
      artist: string
      album: string
      title: string
      trackNumber?: number
    }) => importer.downloadYoutube(jobId, albumId, url, artist, album, title, trackNumber, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      setDownloadingYoutube(null)
      setSearchingYoutubeAlbum(null)
      setYoutubeSearchResults([])
      setYoutubeSearchQuery('')
    },
    onError: () => {
      setDownloadingYoutube(null)
    },
  })

  const skipAlbumMutation = useMutation({
    mutationFn: ({ jobId, albumId }: { jobId: string; albumId: string }) =>
      importer.skipAlbum(jobId, albumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const retryAlbumMutation = useMutation({
    mutationFn: ({ jobId, albumId }: { jobId: string; albumId: string }) =>
      importer.retryAlbum(jobId, albumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const stopAlbumMutation = useMutation({
    mutationFn: ({ jobId, albumId }: { jobId: string; albumId: string }) =>
      importer.stopAlbum(jobId, albumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  // Job control mutations
  const pauseJobMutation = useMutation({
    mutationFn: importer.pauseJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const resumeJobMutation = useMutation({
    mutationFn: importer.resumeJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const cancelJobMutation = useMutation({
    mutationFn: importer.cancelJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const restartJobMutation = useMutation({
    mutationFn: importer.restartJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
    },
  })

  const scanDownloadedMutation = useMutation({
    mutationFn: importer.scanDownloaded,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      alert(`Scan complete! Found ${result.total_folders} folders, matched ${result.matched} albums.${result.matches.length > 0 ? '\n\nMatched:\n' + result.matches.map(m => `• ${m.artist_name} - ${m.album_name}`).join('\n') : ''}`)
    },
    onError: (error: any) => {
      alert(`Scan failed: ${error?.response?.data || error.message}`)
    },
  })

  // Sync mutation - runs in background, progress tracked via polling
  const [syncingJobId, setSyncingJobId] = useState<string | null>(null)
  const [initialSyncCount, setInitialSyncCount] = useState(0)

  const syncDownloadedMutation = useMutation({
    mutationFn: async (jobId: string) => {
      return importer.syncDownloaded(jobId)
    },
    onSuccess: (result: { message: string; albums_to_sync: number }) => {
      if (result.albums_to_sync > 0) {
        setSyncingJobId(syncDownloadedMutation.variables as string)
        setInitialSyncCount(result.albums_to_sync)
      } else {
        alert(result.message)
      }
    },
    onError: (error: any) => {
      alert(`Sync failed: ${error?.response?.data || error.message}`)
    },
  })

  // Calculate sync progress from polled job data
  const getSyncProgress = (job: ImportJobWithDetails) => {
    if (!syncingJobId || syncingJobId !== job.id) return null

    const albums = job.albums || []
    const syncing = albums.filter(a => a.status === 'syncing')
    const synced = albums.filter(a => a.status === 'synced' || a.status === 'already_synced').length
    const downloaded = albums.filter(a => a.status === 'downloaded').length
    const remaining = downloaded + syncing.length

    // Check if sync is complete (no more albums in downloaded or syncing state)
    if (initialSyncCount > 0 && remaining === 0) {
      // Sync complete!
      setTimeout(() => {
        setSyncingJobId(null)
        setInitialSyncCount(0)
        alert(`Sync complete! ${synced} albums synced.`)
      }, 500)
    }

    return {
      current: initialSyncCount - remaining,
      total: initialSyncCount,
      currentAlbum: syncing.length > 0
        ? `${syncing[0].artist_name} - ${syncing[0].album_name}`
        : remaining > 0 ? 'Waiting...' : 'Complete!',
      synced: initialSyncCount - remaining,
      remaining,
    }
  }

  // Create playlist from import
  const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState<string | null>(null) // job id
  const [newPlaylistName, setNewPlaylistName] = useState('')
  const [likeTracks, setLikeTracks] = useState(false)
  const [hideSyncedAlbums, setHideSyncedAlbums] = useState(true) // Hide synced albums by default

  const createPlaylistMutation = useMutation({
    mutationFn: async ({ jobId, playlistName, likeTracks }: { jobId: string; playlistName: string; likeTracks: boolean }) => {
      return importer.createPlaylistFromImport(jobId, playlistName, undefined, likeTracks)
    },
    onSuccess: (result) => {
      setShowCreatePlaylistModal(null)
      setNewPlaylistName('')
      setLikeTracks(false)
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      queryClient.invalidateQueries({ queryKey: ['likedTracks'] })
      let message = `Playlist "${result.playlist_name}" created!\n\n` +
        `Total tracks: ${result.total_tracks}\n` +
        `In library: ${result.matched_tracks}\n` +
        `Missing (pending): ${result.pending_tracks}`
      if (result.liked_tracks > 0) {
        message += `\nLiked: ${result.liked_tracks}`
      }
      alert(message)
    },
    onError: (error: any) => {
      alert(`Failed to create playlist: ${error?.response?.data || error.message}`)
    },
  })

  const likeMatchedTracksMutation = useMutation({
    mutationFn: (jobId: string) => importer.likeMatchedTracks(jobId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['importJobs'] })
      queryClient.invalidateQueries({ queryKey: ['likedTracks'] })
      alert(`Liked ${result.liked_count} new tracks!\n\n` +
        `Already liked: ${result.already_liked}\n` +
        `Total in library: ${result.total_matched}\n\n` +
        `Note: Only tracks that exist in your library can be liked. ` +
        `Download missing tracks first to like them.`)
    },
    onError: (error: any) => {
      alert(`Failed to like tracks: ${error?.response?.data || error.message}`)
    },
  })

  const handleImport = (e: React.FormEvent) => {
    e.preventDefault()
    if (playlistUrl.trim()) {
      importMutation.mutate(playlistUrl.trim())
    }
  }

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault()
    saveSettingsMutation.mutate()
  }

  const handleSearch = (jobId: string, album: ImportJobAlbum) => {
    setSearchingAlbum(album.id)
    setSearchResults([])
    searchAlbumMutation.mutate({ jobId, albumId: album.id })
  }

  const handleDabSearch = (_jobId: string, album: ImportJobAlbum) => {
    // Open manual search input for this album with default query
    setDabManualSearchAlbum(album.id)
    setDabSearchQuery(`${album.artist_name} ${album.album_name}`)
    setSearchingDabAlbum(null)
    setDabSearchResults([])
  }

  const handleDabManualSearch = (jobId: string, albumId: string) => {
    if (!dabSearchQuery.trim()) return
    setSearchingDabAlbum(albumId)
    searchAlbumDabMutation.mutate({ jobId, albumId, query: dabSearchQuery.trim() })
  }

  // YouTube handlers
  const handleYoutubeSearch = (_jobId: string, album: ImportJobAlbum) => {
    // Open YouTube search input for this album with default query
    setSearchingYoutubeAlbum(album.id)
    setYoutubeSearchQuery(`${album.artist_name} ${album.album_name}`)
    setYoutubeSearchResults([])
  }

  const handleYoutubeManualSearch = () => {
    if (!youtubeSearchQuery.trim()) return
    searchYoutubeMutation.mutate({ query: youtubeSearchQuery.trim() })
  }

  const handleYoutubeDownload = (
    jobId: string,
    album: ImportJobAlbum,
    result: YtSearchResult
  ) => {
    setDownloadingYoutube(result.id)
    downloadYoutubeMutation.mutate({
      jobId,
      albumId: album.id,
      url: result.url,
      artist: album.artist_name,
      album: album.album_name,
      title: album.album_name, // Use album name as track title for single tracks
    })
  }

  const getJobProgress = (job: ImportJobWithDetails) => {
    if (!job.albums || job.albums.length === 0) return 0

    // Count albums in different states
    const searched = job.albums.filter(
      (a) => a.status !== 'pending'
    ).length
    const completed = job.albums.filter(
      (a) => a.status === 'completed' || a.status === 'skipped' || a.status === 'synced' || a.status === 'already_synced'
    ).length
    const downloading = job.albums.filter(
      (a) => a.status === 'downloading' || a.status === 'syncing' || a.status === 'dab_downloading'
    ).length

    const total = job.albums.length

    // Phase-based progress:
    // - Parsing: 0-5%
    // - Searching: 5-40% (based on albums searched)
    // - Downloading: 40-90% (based on albums downloading/completed)
    // - Completed: 100%

    if (job.status === 'parsing') {
      return 2
    } else if (job.status === 'searching') {
      // During search phase, progress is 5-40%
      const searchProgress = total > 0 ? (searched / total) : 0
      return Math.round(5 + (searchProgress * 35))
    } else if (job.status === 'downloading' || job.status === 'syncing') {
      // During download phase, progress is 40-90%
      const downloadProgress = total > 0 ? ((completed + downloading * 0.5) / total) : 0
      return Math.round(40 + (downloadProgress * 50))
    } else if (job.status === 'completed') {
      return 100
    } else if (job.status === 'failed' || job.status === 'cancelled') {
      // Show how far we got
      const progress = total > 0 ? (completed / total) : 0
      return Math.round(progress * 100)
    }

    // Default: show completion progress
    return total > 0 ? Math.round((completed / total) * 100) : 0
  }

  const getJobStatusText = (job: ImportJobWithDetails) => {
    const total = job.albums?.length || 0
    const searched = job.albums?.filter(a => a.status !== 'pending').length || 0
    const downloading = job.albums?.filter(a => a.status === 'downloading' || a.status === 'dab_downloading').length || 0
    const synced = job.albums?.filter(a => a.status === 'synced' || a.status === 'completed' || a.status === 'skipped' || a.status === 'already_synced').length || 0

    switch (job.status) {
      case 'parsing':
        return 'Parsing playlist...'
      case 'searching':
        return `Searching ${searched}/${total} albums`
      case 'downloading':
        return `Downloading ${downloading} active, ${synced}/${total} done`
      case 'syncing':
        return `Syncing files...`
      case 'completed':
        return `Completed: ${synced}/${total} albums`
      case 'paused':
        return `Paused (${searched}/${total} searched)`
      case 'cancelled':
        return 'Cancelled'
      case 'failed':
        return `Failed: ${job.error_message || 'Unknown error'}`
      default:
        return job.status
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
          <Music className="text-green-500" />
          Spotify Importer
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-dark-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('connect')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
            activeTab === 'connect'
              ? 'text-green-400 border-b-2 border-green-400'
              : 'text-dark-400 hover:text-dark-200'
          }`}
        >
          <ExternalLink size={16} />
          Connect
          {spotifyToken && <CheckCircle size={14} className="text-green-400" />}
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'import'
              ? 'text-primary-400 border-b-2 border-primary-400'
              : 'text-dark-400 hover:text-dark-200'
          }`}
        >
          Manual Import
        </button>
        <button
          onClick={() => setActiveTab('jobs')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'jobs'
              ? 'text-primary-400 border-b-2 border-primary-400'
              : 'text-dark-400 hover:text-dark-200'
          }`}
        >
          Jobs
          {jobs && jobs.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-primary-600/30 rounded-full">
              {jobs.length}
            </span>
          )}
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-dark-400 hover:text-dark-200'
            }`}
          >
            Settings
          </button>
        )}
      </div>

      {/* Connect Tab */}
      {activeTab === 'connect' && (
        <div className="space-y-6">
          {!spotifyToken ? (
            /* Not connected - show login form */
            <div className="card">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <ExternalLink size={20} className="text-green-400" />
                Connect to Spotify
              </h2>
              <p className="text-dark-400 mb-4">
                Connect your Spotify account to browse and import your playlists directly.
              </p>

              {spotifyError && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm mb-4">
                  {spotifyError}
                </div>
              )}

              {configuredClientId ? (
                /* Client ID is configured in settings - simple connect button */
                <button
                  onClick={handleSpotifyConnect}
                  className="btn bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 py-3 px-6"
                >
                  <ExternalLink size={18} />
                  Connect to Spotify
                </button>
              ) : (
                /* No client ID configured - show setup instructions */
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 font-medium mb-2">Setup Required</p>
                    <p className="text-dark-300 text-sm">
                      {isAdmin
                        ? 'You need to configure the Spotify Client ID in the Settings tab first.'
                        : 'An admin needs to configure the Spotify Client ID before you can use this feature.'}
                    </p>
                  </div>
                  {isAdmin && (
                    <>
                      <div className="text-sm text-dark-400 space-y-2">
                        <p className="font-medium text-dark-300">Setup instructions:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Go to{' '}
                            <a
                              href="https://developer.spotify.com/dashboard"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-400 hover:underline"
                            >
                              developer.spotify.com/dashboard
                            </a>
                          </li>
                          <li>Create a new app (any name)</li>
                          <li>Add this redirect URI: <code className="bg-dark-700 px-1 rounded">{window.location.origin}/spotify-callback</code></li>
                          <li>Copy the Client ID and paste it in the Settings tab</li>
                        </ol>
                      </div>
                      <button
                        onClick={() => setActiveTab('settings')}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        <Settings size={18} />
                        Go to Settings
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : spotifyLoading ? (
            /* Loading playlists */
            <div className="card text-center py-12">
              <RefreshCw size={48} className="text-green-400 mx-auto mb-4 animate-spin" />
              <p className="text-dark-400">Loading your Spotify playlists...</p>
            </div>
          ) : (
            /* Connected - show playlists */
            <>
              {/* User info header */}
              <div className="card flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {spotifyUser?.images?.[0]?.url ? (
                    <img
                      src={spotifyUser.images[0].url}
                      alt={spotifyUser.display_name}
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center text-white font-bold">
                      {spotifyUser?.display_name?.[0] || '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{spotifyUser?.display_name}</p>
                    <p className="text-sm text-dark-400">
                      {spotifyPlaylists.length} playlists
                      {likedSongsCount !== null && ` · ${likedSongsCount} liked songs`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSpotifyDisconnect}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <LogOut size={16} />
                  Disconnect
                </button>
              </div>

              {spotifyError && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {spotifyError}
                </div>
              )}

              {/* Selection controls */}
              <div className="card">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-semibold">Select Playlists to Import</h3>
                    <p className="text-sm text-dark-400">
                      {selectedPlaylists.size} playlists selected
                      {includeLikedSongs && ' + Liked Songs'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={selectAllPlaylists} className="btn btn-secondary text-sm">
                      Select All
                    </button>
                    <button onClick={deselectAllPlaylists} className="btn btn-secondary text-sm">
                      Clear
                    </button>
                  </div>
                </div>

                {/* Liked Songs option */}
                <label className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-600/20 to-purple-600/20 rounded-lg mb-3 cursor-pointer hover:from-pink-600/30 hover:to-purple-600/30">
                  <input
                    type="checkbox"
                    checked={includeLikedSongs}
                    onChange={(e) => setIncludeLikedSongs(e.target.checked)}
                    className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-green-500 focus:ring-green-500"
                  />
                  <Heart size={20} className="text-pink-400" />
                  <div className="flex-1">
                    <p className="font-medium">Liked Songs</p>
                    <p className="text-sm text-dark-400">{likedSongsCount || 0} songs</p>
                  </div>
                </label>

                {/* Playlist list */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {spotifyPlaylists.map((playlist) => (
                    <label
                      key={playlist.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedPlaylists.has(playlist.id)
                          ? 'bg-green-600/20'
                          : 'bg-dark-700 hover:bg-dark-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlaylists.has(playlist.id)}
                        onChange={() => togglePlaylistSelection(playlist.id)}
                        className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-green-500 focus:ring-green-500"
                      />
                      {playlist.images?.[0]?.url ? (
                        <img
                          src={playlist.images[0].url}
                          alt={playlist.name}
                          className="w-10 h-10 rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-dark-600 flex items-center justify-center">
                          <Music size={20} className="text-dark-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{playlist.name}</p>
                        <p className="text-sm text-dark-400">
                          {playlist.tracks.total} tracks · by {playlist.owner.display_name}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Import button */}
              <button
                onClick={handleImportSelected}
                disabled={importingPlaylists || (selectedPlaylists.size === 0 && !includeLikedSongs)}
                className="btn bg-green-600 hover:bg-green-700 text-white w-full flex items-center justify-center gap-2 py-3"
              >
                {importingPlaylists ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Importing {importProgress?.name}... ({importProgress?.current}/{importProgress?.total})
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Import {selectedPlaylists.size + (includeLikedSongs ? 1 : 0)} Playlist{(selectedPlaylists.size + (includeLikedSongs ? 1 : 0)) !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Link size={20} />
              Import Spotify Playlist (Manual)
            </h2>
            <form onSubmit={handleImport} className="space-y-4">
              {importMutation.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {(importMutation.error as any)?.response?.data?.error ||
                    'Failed to start import'}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Spotify Playlist URL
                </label>
                <input
                  type="url"
                  value={playlistUrl}
                  onChange={(e) => setPlaylistUrl(e.target.value)}
                  className="input"
                  placeholder="https://open.spotify.com/playlist/..."
                  required
                />
                <p className="text-dark-500 text-sm mt-1">
                  Paste a public Spotify playlist URL. The importer will extract
                  tracks, group them by album, and search for downloads.
                </p>
              </div>
              <button
                type="submit"
                disabled={importMutation.isPending || !playlistUrl.trim()}
                className="btn btn-primary flex items-center gap-2"
              >
                {importMutation.isPending ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Starting Import...
                  </>
                ) : (
                  <>
                    <Play size={18} />
                    Start Import
                  </>
                )}
              </button>
            </form>
          </div>

          {/* CSV Import */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Upload size={20} />
              Import from CSV/TSV File
            </h2>
            <div className="space-y-4">
              <p className="text-dark-400 text-sm">
                Export your Spotify playlist using{' '}
                <a
                  href="https://exportify.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:underline"
                >
                  Exportify
                </a>{' '}
                or similar tools, then upload the CSV/TSV file here. This method supports playlists of any size.
              </p>
              {importCsvMutation.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {(importCsvMutation.error as any)?.response?.data?.error ||
                    'Failed to import CSV'}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  CSV/TSV File
                </label>
                <input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-dark-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-dark-700 file:text-dark-200 hover:file:bg-dark-600"
                />
                {csvFile && (
                  <p className="text-dark-400 text-sm mt-1">
                    Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <button
                onClick={() => csvFile && importCsvMutation.mutate(csvFile)}
                disabled={importCsvMutation.isPending || !csvFile}
                className="btn btn-primary flex items-center gap-2"
              >
                {importCsvMutation.isPending ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Import CSV
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quick status */}
          {jobs && jobs.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold mb-3">Recent Jobs</h3>
              <div className="space-y-2">
                {jobs.slice(0, 3).map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-3 bg-dark-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{job.playlist_name}</p>
                      <p className="text-sm text-dark-400">
                        {getJobStatusText(job)}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setActiveTab('jobs')
                        setExpandedJob(job.id)
                      }}
                      className="btn btn-secondary text-sm"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="space-y-4">
          {jobsLoading ? (
            <div className="text-dark-400">Loading jobs...</div>
          ) : jobs && jobs.length > 0 ? (
            jobs.map((job) => (
              <div key={job.id} className="card">
                <div
                  className="flex items-center justify-between cursor-pointer gap-4"
                  onClick={() =>
                    setExpandedJob(expandedJob === job.id ? null : job.id)
                  }
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Music size={20} className="text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium truncate">{job.playlist_name}</h3>
                      <p className="text-sm text-dark-400">
                        {job.unique_albums} albums · {job.total_tracks} tracks
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-dark-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 transition-all"
                            style={{ width: `${getJobProgress(job)}%` }}
                          />
                        </div>
                        <span className="text-sm text-dark-400 w-8">
                          {getJobProgress(job)}%
                        </span>
                      </div>
                      <p className={`text-xs ${statusColors[job.status]} whitespace-nowrap`}>
                        {getJobStatusText(job)}
                      </p>
                    </div>
                    {/* Job control buttons */}
                    <div className="flex items-center gap-1">
                      {/* Pause/Resume */}
                      {job.status === 'paused' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            resumeJobMutation.mutate(job.id)
                          }}
                          className="btn btn-secondary p-2"
                          title="Resume"
                        >
                          <Play size={16} />
                        </button>
                      ) : !['completed', 'cancelled', 'failed'].includes(job.status) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            pauseJobMutation.mutate(job.id)
                          }}
                          className="btn btn-secondary p-2"
                          title="Pause"
                        >
                          <Pause size={16} />
                        </button>
                      )}
                      {/* Restart */}
                      {['completed', 'failed', 'cancelled'].includes(job.status) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            restartJobMutation.mutate(job.id)
                          }}
                          className="btn btn-secondary p-2"
                          title="Restart"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {/* Scan Seedbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          scanDownloadedMutation.mutate(job.id)
                        }}
                        disabled={scanDownloadedMutation.isPending}
                        className="btn btn-secondary px-3 py-2 flex items-center gap-2"
                        title="Scan Seedbox - match downloaded albums"
                      >
                        {scanDownloadedMutation.isPending ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : (
                          <FolderSearch size={16} />
                        )}
                        <span className="text-sm">Scan</span>
                      </button>
                      {/* Sync to Local */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          syncDownloadedMutation.mutate(job.id)
                        }}
                        disabled={syncDownloadedMutation.isPending || syncingJobId !== null}
                        className="btn bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-2 flex items-center gap-2"
                        title="Sync downloaded albums to local storage"
                      >
                        {syncDownloadedMutation.isPending || syncingJobId === job.id ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : (
                          <FolderSync size={16} />
                        )}
                        <span className="text-sm">Sync</span>
                      </button>
                      {/* Create Playlist */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setNewPlaylistName(job.playlist_name || 'Imported Playlist')
                          setShowCreatePlaylistModal(job.id)
                        }}
                        className="btn bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 px-3 py-2 flex items-center gap-2"
                        title="Create playlist from import"
                      >
                        <ListMusic size={16} />
                        <span className="text-sm">Playlist</span>
                      </button>
                      {/* Like Matched Tracks */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          likeMatchedTracksMutation.mutate(job.id)
                        }}
                        disabled={likeMatchedTracksMutation.isPending}
                        className="btn bg-pink-600/20 hover:bg-pink-600/30 text-pink-400 px-3 py-2 flex items-center gap-2"
                        title="Add all matched tracks to liked songs"
                      >
                        <Heart size={16} />
                        <span className="text-sm">Like</span>
                      </button>
                      {/* Cancel */}
                      {!['completed', 'cancelled'].includes(job.status) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (confirm('Cancel this import job?')) {
                              cancelJobMutation.mutate(job.id)
                            }
                          }}
                          className="btn bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 p-2"
                          title="Cancel"
                        >
                          <Square size={16} />
                        </button>
                      )}
                      {/* Logs */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowLogsFor(job.id)
                        }}
                        className="btn btn-secondary p-2"
                        title="View Logs"
                      >
                        <FileText size={16} />
                      </button>
                      {/* Delete */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('Delete this import job?')) {
                            deleteJobMutation.mutate(job.id)
                          }
                        }}
                        className="btn bg-red-600/20 hover:bg-red-600/30 text-red-400 p-2"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {expandedJob === job.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </div>
                </div>

                {/* Expanded album list */}
                {expandedJob === job.id && job.albums && (
                  <div className="mt-4 pt-4 border-t border-dark-700 space-y-2">
                    {/* Filter controls */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-dark-700">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hideSyncedAlbums}
                          onChange={(e) => setHideSyncedAlbums(e.target.checked)}
                          className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-accent-500 focus:ring-accent-500 focus:ring-offset-dark-900"
                        />
                        <span className="text-sm text-dark-300">Hide synced albums</span>
                      </label>
                      <span className="text-sm text-dark-400">
                        Showing {job.albums.filter(a => !hideSyncedAlbums || (a.status !== 'synced' && a.status !== 'completed' && a.status !== 'skipped' && a.status !== 'already_synced')).length} of {job.albums.length} albums
                      </span>
                    </div>
                    {job.albums
                      .filter(album => !hideSyncedAlbums || (album.status !== 'synced' && album.status !== 'completed' && album.status !== 'skipped' && album.status !== 'already_synced'))
                      .map((album) => (
                      <div
                        key={album.id}
                        className={`p-3 rounded-lg ${
                          album.status === 'not_found'
                            ? 'bg-orange-900/20 border border-orange-600/30'
                            : album.status === 'failed'
                            ? 'bg-red-900/20 border border-red-600/30'
                            : album.status === 'stopped'
                            ? 'bg-orange-900/20 border border-orange-600/30'
                            : album.status === 'downloading' || album.status === 'dab_downloading' || album.status === 'slskd_downloading' || album.status === 'yt_downloading'
                            ? 'bg-blue-900/20 border border-blue-600/30'
                            : 'bg-dark-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={statusColors[album.status]}>
                              {statusIcons[album.status]}
                            </span>
                            <div>
                              <p className="font-medium">
                                {album.artist_name} - {album.album_name}
                              </p>
                              <p className="text-sm text-dark-400">
                                {album.track_count} tracks ·{' '}
                                <span className={statusColors[album.status]}>
                                  {album.status}
                                </span>
                                {album.error_message && (
                                  <span className="text-red-400">
                                    {' '}
                                    - {album.error_message}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {(album.status === 'not_found' ||
                              album.status === 'failed' ||
                              album.status === 'pending') && (
                              <>
                                <button
                                  onClick={() => handleSearch(job.id, album)}
                                  disabled={searchAlbumMutation.isPending}
                                  className="btn btn-secondary text-sm flex items-center gap-1"
                                >
                                  <Search size={14} />
                                  Search
                                </button>
                                {settings.dab_enabled === 1 && (
                                  <button
                                    onClick={() => handleDabSearch(job.id, album)}
                                    disabled={searchAlbumDabMutation.isPending}
                                    className="btn bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-sm flex items-center gap-1"
                                  >
                                    <Music size={14} />
                                    DAB
                                  </button>
                                )}
                                <button
                                  onClick={() => handleYoutubeSearch(job.id, album)}
                                  disabled={searchYoutubeMutation.isPending}
                                  className="btn bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm flex items-center gap-1"
                                >
                                  <Youtube size={14} />
                                  YT
                                </button>
                                <button
                                  onClick={() =>
                                    skipAlbumMutation.mutate({
                                      jobId: job.id,
                                      albumId: album.id,
                                    })
                                  }
                                  className="btn btn-secondary text-sm flex items-center gap-1"
                                >
                                  <SkipForward size={14} />
                                  Skip
                                </button>
                              </>
                            )}
                            {album.status === 'failed' && (
                              <button
                                onClick={() =>
                                  retryAlbumMutation.mutate({
                                    jobId: job.id,
                                    albumId: album.id,
                                  })
                                }
                                className="btn btn-secondary text-sm flex items-center gap-1"
                              >
                                <RefreshCw size={14} />
                                Retry
                              </button>
                            )}
                            {/* Stop button for downloading albums */}
                            {(album.status === 'downloading' ||
                              album.status === 'dab_downloading' ||
                              album.status === 'slskd_downloading' ||
                              album.status === 'yt_downloading') && (
                              <button
                                onClick={() =>
                                  stopAlbumMutation.mutate({
                                    jobId: job.id,
                                    albumId: album.id,
                                  })
                                }
                                disabled={stopAlbumMutation.isPending}
                                className="btn bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 text-sm flex items-center gap-1"
                              >
                                <Square size={14} />
                                Stop
                              </button>
                            )}
                            {/* Retry button for stopped albums */}
                            {album.status === 'stopped' && (
                              <>
                                <button
                                  onClick={() =>
                                    retryAlbumMutation.mutate({
                                      jobId: job.id,
                                      albumId: album.id,
                                    })
                                  }
                                  disabled={retryAlbumMutation.isPending}
                                  className="btn btn-secondary text-sm flex items-center gap-1"
                                >
                                  <RefreshCw size={14} />
                                  Retry
                                </button>
                                <button
                                  onClick={() => handleSearch(job.id, album)}
                                  disabled={searchAlbumMutation.isPending}
                                  className="btn btn-secondary text-sm flex items-center gap-1"
                                >
                                  <Search size={14} />
                                  Search
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Search results modal */}
                        {searchingAlbum === album.id && (
                          <div className="mt-3 pt-3 border-t border-dark-600">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">Search Results</h4>
                              <button
                                onClick={() => {
                                  setSearchingAlbum(null)
                                  setSearchResults([])
                                }}
                                className="p-1 hover:bg-dark-600 rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            {searchAlbumMutation.isPending ? (
                              <div className="flex items-center gap-2 text-dark-400">
                                <RefreshCw
                                  size={16}
                                  className="animate-spin"
                                />
                                Searching...
                              </div>
                            ) : searchResults.length > 0 ? (
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {searchResults.map((result, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between p-2 bg-dark-800 rounded hover:bg-dark-600 cursor-pointer"
                                    onClick={() =>
                                      selectResultMutation.mutate({
                                        jobId: job.id,
                                        albumId: album.id,
                                        guid: result.guid,
                                        indexerId: result.indexer_id,
                                      })
                                    }
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">
                                        {result.title}
                                      </p>
                                      <p className="text-xs text-dark-400">
                                        {result.size
                                          ? `${(
                                              result.size /
                                              1024 /
                                              1024
                                            ).toFixed(0)} MB`
                                          : 'Size unknown'}{' '}
                                        · {result.indexer_name || 'Unknown indexer'}
                                        {result.seeders !== undefined && (
                                          <> · {result.seeders} seeders</>
                                        )}
                                      </p>
                                    </div>
                                    <button className="btn btn-primary text-sm ml-2">
                                      <Download size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-dark-400 text-sm">
                                No results found. Try a manual search or skip
                                this album.
                              </p>
                            )}
                          </div>
                        )}

                        {/* DAB Manual Search Input */}
                        {dabManualSearchAlbum === album.id && (
                          <div className="mt-3 pt-3 border-t border-purple-600/30">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-purple-400">DAB Search</h4>
                              <button
                                onClick={() => {
                                  setDabManualSearchAlbum(null)
                                  setDabSearchQuery('')
                                  setSearchingDabAlbum(null)
                                  setDabSearchResults([])
                                }}
                                className="p-1 hover:bg-dark-600 rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            <form
                              onSubmit={(e) => {
                                e.preventDefault()
                                handleDabManualSearch(job.id, album.id)
                              }}
                              className="flex gap-2 mb-3"
                            >
                              <input
                                type="text"
                                value={dabSearchQuery}
                                onChange={(e) => setDabSearchQuery(e.target.value)}
                                className="input flex-1 text-sm"
                                placeholder="Search DAB for artist, album..."
                                autoFocus
                              />
                              <button
                                type="submit"
                                disabled={searchAlbumDabMutation.isPending || !dabSearchQuery.trim()}
                                className="btn bg-purple-600 hover:bg-purple-700 text-white text-sm flex items-center gap-1"
                              >
                                {searchAlbumDabMutation.isPending ? (
                                  <RefreshCw size={14} className="animate-spin" />
                                ) : (
                                  <Search size={14} />
                                )}
                                Search
                              </button>
                            </form>
                            {/* Search results within the same container */}
                            {searchingDabAlbum === album.id && (
                              <>
                                {searchAlbumDabMutation.isPending ? (
                                  <div className="flex items-center gap-2 text-purple-400">
                                    <RefreshCw size={16} className="animate-spin" />
                                    Searching DAB...
                                  </div>
                                ) : dabSearchResults.length > 0 ? (
                                  <div className="space-y-3 max-h-80 overflow-y-auto">
                                    {dabSearchResults.map((result, idx) => (
                                      <div
                                        key={idx}
                                        className="flex gap-3 p-3 bg-purple-900/20 rounded-lg hover:bg-purple-900/30 cursor-pointer border border-purple-600/20"
                                        onClick={() =>
                                          selectDabResultMutation.mutate({
                                            jobId: job.id,
                                            albumId: album.id,
                                            dabAlbumId: result.album_id,
                                          })
                                        }
                                      >
                                        {/* Album Cover */}
                                        {result.cover_url && (
                                          <img
                                            src={result.cover_url}
                                            alt={result.title}
                                            className="w-16 h-16 rounded object-cover flex-shrink-0"
                                          />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="font-medium truncate text-white">
                                            {result.title}
                                          </p>
                                          <p className="text-sm text-purple-400 truncate">
                                            {result.artist}
                                          </p>
                                          {/* Metadata badges */}
                                          <div className="flex flex-wrap gap-1.5 mt-2">
                                            {result.genre && (
                                              <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                                {result.genre}
                                              </span>
                                            )}
                                            {result.release_date && (
                                              <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                                {result.release_date}
                                              </span>
                                            )}
                                            <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                              {result.track_count} tracks
                                            </span>
                                            {result.is_hi_res && (
                                              <span className="text-xs px-2 py-0.5 bg-purple-600 rounded-full text-white font-medium">
                                                Hi-Res
                                              </span>
                                            )}
                                          </div>
                                          {/* Quality info */}
                                          <p className="text-xs text-dark-500 mt-1.5">
                                            {result.bit_depth}bit / {result.sample_rate}kHz
                                            {result.label && ` · ${result.label}`}
                                          </p>
                                          {/* Track titles for small albums */}
                                          {result.track_titles && result.track_titles.length > 0 && (
                                            <p className="text-xs text-purple-400 mt-1 truncate">
                                              Tracks: {result.track_titles.join(', ')}
                                            </p>
                                          )}
                                        </div>
                                        <button className="btn bg-purple-600 hover:bg-purple-700 text-white text-sm self-center flex-shrink-0">
                                          <Download size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-dark-400 text-sm">
                                    No DAB results found. Try a different search term.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* DAB Search results modal (legacy - for auto-search, can be removed) */}
                        {searchingDabAlbum === album.id && dabManualSearchAlbum !== album.id && (
                          <div className="mt-3 pt-3 border-t border-purple-600/30">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-purple-400">DAB Search Results</h4>
                              <button
                                onClick={() => {
                                  setSearchingDabAlbum(null)
                                  setDabSearchResults([])
                                }}
                                className="p-1 hover:bg-dark-600 rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            {searchAlbumDabMutation.isPending ? (
                              <div className="flex items-center gap-2 text-purple-400">
                                <RefreshCw
                                  size={16}
                                  className="animate-spin"
                                />
                                Searching DAB...
                              </div>
                            ) : dabSearchResults.length > 0 ? (
                              <div className="space-y-3 max-h-80 overflow-y-auto">
                                {dabSearchResults.map((result, idx) => (
                                  <div
                                    key={idx}
                                    className="flex gap-3 p-3 bg-purple-900/20 rounded-lg hover:bg-purple-900/30 cursor-pointer border border-purple-600/20"
                                    onClick={() =>
                                      selectDabResultMutation.mutate({
                                        jobId: job.id,
                                        albumId: album.id,
                                        dabAlbumId: result.album_id,
                                      })
                                    }
                                  >
                                    {/* Album Cover */}
                                    {result.cover_url && (
                                      <img
                                        src={result.cover_url}
                                        alt={result.title}
                                        className="w-16 h-16 rounded object-cover flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate text-white">
                                        {result.title}
                                      </p>
                                      <p className="text-sm text-purple-400 truncate">
                                        {result.artist}
                                      </p>
                                      {/* Metadata badges */}
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {result.genre && (
                                          <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                            {result.genre}
                                          </span>
                                        )}
                                        {result.release_date && (
                                          <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                            {result.release_date}
                                          </span>
                                        )}
                                        <span className="text-xs px-2 py-0.5 bg-dark-600 rounded-full text-dark-300">
                                          {result.track_count} tracks
                                        </span>
                                        {result.is_hi_res && (
                                          <span className="text-xs px-2 py-0.5 bg-purple-600 rounded-full text-white font-medium">
                                            Hi-Res
                                          </span>
                                        )}
                                      </div>
                                      {/* Quality info */}
                                      <p className="text-xs text-dark-500 mt-1.5">
                                        {result.bit_depth}bit / {result.sample_rate}kHz
                                        {result.label && ` · ${result.label}`}
                                      </p>
                                      {/* Track titles for small albums */}
                                      {result.track_titles && result.track_titles.length > 0 && (
                                        <p className="text-xs text-purple-400 mt-1 truncate">
                                          Tracks: {result.track_titles.join(', ')}
                                        </p>
                                      )}
                                    </div>
                                    <button className="btn bg-purple-600 hover:bg-purple-700 text-white text-sm self-center flex-shrink-0">
                                      <Download size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-dark-400 text-sm">
                                No DAB results found for this album.
                              </p>
                            )}
                          </div>
                        )}

                        {/* YouTube Search Panel */}
                        {searchingYoutubeAlbum === album.id && (
                          <div className="mt-3 pt-3 border-t border-red-600/30">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-red-400">YouTube Search</h4>
                              <button
                                onClick={() => {
                                  setSearchingYoutubeAlbum(null)
                                  setYoutubeSearchResults([])
                                  setYoutubeSearchQuery('')
                                }}
                                className="p-1 hover:bg-dark-600 rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            <form
                              onSubmit={(e) => {
                                e.preventDefault()
                                handleYoutubeManualSearch()
                              }}
                              className="flex gap-2 mb-3"
                            >
                              <input
                                type="text"
                                value={youtubeSearchQuery}
                                onChange={(e) => setYoutubeSearchQuery(e.target.value)}
                                className="input flex-1 text-sm"
                                placeholder="Search YouTube for artist, track..."
                                autoFocus
                              />
                              <button
                                type="submit"
                                disabled={searchYoutubeMutation.isPending || !youtubeSearchQuery.trim()}
                                className="btn bg-red-600 hover:bg-red-700 text-white text-sm flex items-center gap-1"
                              >
                                {searchYoutubeMutation.isPending ? (
                                  <RefreshCw size={14} className="animate-spin" />
                                ) : (
                                  <Search size={14} />
                                )}
                                Search
                              </button>
                            </form>
                            {/* Download status */}
                            {downloadYoutubeMutation.isPending && (
                              <div className="mb-3 p-3 bg-red-900/30 border border-red-600/30 rounded-lg">
                                <div className="flex items-center gap-2 text-red-400">
                                  <RefreshCw size={16} className="animate-spin" />
                                  <div>
                                    <p className="font-medium">Downloading from YouTube...</p>
                                    <p className="text-xs text-red-400/70">This may take a moment. Converting to FLAC and adding metadata.</p>
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* YouTube search results */}
                            {searchYoutubeMutation.isPending ? (
                              <div className="flex items-center gap-2 text-red-400">
                                <RefreshCw size={16} className="animate-spin" />
                                Searching YouTube...
                              </div>
                            ) : youtubeSearchResults.length > 0 ? (
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {youtubeSearchResults.map((result) => (
                                  <div
                                    key={result.id}
                                    className={`flex items-center justify-between p-2 rounded border ${
                                      downloadingYoutube === result.id
                                        ? 'bg-red-900/40 border-red-500/50'
                                        : 'bg-red-900/20 hover:bg-red-900/30 border-red-600/20'
                                    }`}
                                  >
                                    {/* Thumbnail */}
                                    {result.thumbnail && (
                                      <img
                                        src={result.thumbnail}
                                        alt={result.title}
                                        className="w-16 h-12 rounded object-cover flex-shrink-0 mr-3"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">
                                        {result.title}
                                      </p>
                                      <p className="text-xs text-dark-400">
                                        {result.channel} · {result.duration}
                                      </p>
                                      {downloadingYoutube === result.id && (
                                        <p className="text-xs text-red-400 mt-1">Downloading...</p>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleYoutubeDownload(job.id, album, result)}
                                      disabled={downloadingYoutube !== null || downloadYoutubeMutation.isPending}
                                      className={`btn text-white text-sm ml-2 flex items-center gap-1 ${
                                        downloadingYoutube === result.id
                                          ? 'bg-red-700'
                                          : 'bg-red-600 hover:bg-red-700'
                                      }`}
                                    >
                                      {downloadingYoutube === result.id ? (
                                        <RefreshCw size={14} className="animate-spin" />
                                      ) : (
                                        <Download size={14} />
                                      )}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : youtubeSearchQuery && !searchYoutubeMutation.isPending ? (
                              <p className="text-dark-400 text-sm">
                                No YouTube results found. Try a different search term.
                              </p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="card text-center py-12">
              <Music size={48} className="text-dark-500 mx-auto mb-4" />
              <p className="text-dark-400 mb-2">No import jobs</p>
              <p className="text-dark-500 text-sm">
                Start by importing a Spotify playlist
              </p>
            </div>
          )}
        </div>
      )}

      {/* Logs Modal */}
      {showLogsFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileText size={20} />
                Job Logs
              </h2>
              <button
                onClick={() => setShowLogsFor(null)}
                className="p-1 hover:bg-dark-700 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const job = jobs?.find((j) => j.id === showLogsFor)
                if (!job) return <p className="text-dark-400">Job not found</p>
                if (!job.logs || job.logs.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Info size={32} className="text-dark-500 mx-auto mb-2" />
                      <p className="text-dark-400">No logs yet</p>
                      <p className="text-dark-500 text-sm">
                        Logs will appear here as the job progresses
                      </p>
                    </div>
                  )
                }
                return (
                  <div className="space-y-2 font-mono text-sm">
                    {job.logs.map((log) => (
                      <div
                        key={log.id}
                        className={`p-2 bg-dark-700 rounded ${logLevelColors[log.level] || 'text-dark-300'}`}
                      >
                        <div className="flex items-center gap-2 text-xs text-dark-500 mb-1">
                          <span>{new Date(log.created_at).toLocaleTimeString()}</span>
                          <span className={`uppercase ${logLevelColors[log.level]}`}>
                            {log.level}
                          </span>
                        </div>
                        <p>{log.message}</p>
                        {log.details && (
                          <pre className="text-xs text-dark-400 mt-1 overflow-x-auto">
                            {log.details}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
            <div className="p-4 border-t border-dark-700">
              <button
                onClick={() => setShowLogsFor(null)}
                className="btn btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab - Admin Only */}
      {activeTab === 'settings' && isAdmin && (
        <form onSubmit={handleSaveSettings} className="space-y-6">
          {saveSettingsMutation.error && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              Failed to save settings.
            </div>
          )}

          {saveSettingsMutation.isSuccess && (
            <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 text-sm">
              Settings saved successfully
            </div>
          )}

          {/* Spotify Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Music size={20} className="text-green-400" />
              Spotify Integration
            </h2>
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">
                Spotify Client ID
              </label>
              <input
                type="text"
                value={settings.spotify_client_id || ''}
                onChange={(e) =>
                  setSettings({ ...settings, spotify_client_id: e.target.value })
                }
                className="input"
                placeholder="Your Spotify App Client ID"
                disabled={!isAdmin}
              />
              <p className="text-dark-500 text-sm mt-1">
                Create a Spotify App at{' '}
                <a
                  href="https://developer.spotify.com/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:underline"
                >
                  developer.spotify.com/dashboard
                </a>
                {' '}and add <code className="bg-dark-700 px-1 rounded">{window.location.origin}/spotify-callback</code> as a redirect URI.
              </p>
            </div>
          </div>

          {/* Prowlarr Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Search size={20} />
              Prowlarr Settings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Prowlarr URL
                </label>
                <input
                  type="url"
                  value={settings.prowlarr_url || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, prowlarr_url: e.target.value })
                  }
                  className="input"
                  placeholder="http://localhost:9696"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  value={settings.prowlarr_api_key || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, prowlarr_api_key: e.target.value })
                  }
                  className="input"
                  placeholder="API key from Prowlarr settings"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Username (optional)
                </label>
                <input
                  type="text"
                  value={settings.prowlarr_username || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, prowlarr_username: e.target.value })
                  }
                  className="input"
                  placeholder="Basic auth username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Password (optional)
                </label>
                <input
                  type="password"
                  value={settings.prowlarr_password || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, prowlarr_password: e.target.value })
                  }
                  className="input"
                  placeholder="Basic auth password"
                />
              </div>
            </div>
          </div>

          {/* rTorrent Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Download size={20} />
              rTorrent Settings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  rTorrent XML-RPC URL
                </label>
                <input
                  type="url"
                  value={settings.rtorrent_url || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, rtorrent_url: e.target.value })
                  }
                  className="input"
                  placeholder="https://user:pass@seedbox.example.com/rutorrent/plugins/rpc/rpc.php"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Username (optional)
                </label>
                <input
                  type="text"
                  value={settings.rtorrent_username || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, rtorrent_username: e.target.value })
                  }
                  className="input"
                  placeholder="Username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Password (optional)
                </label>
                <input
                  type="password"
                  value={settings.rtorrent_password || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, rtorrent_password: e.target.value })
                  }
                  className="input"
                  placeholder="Password"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Music Label
                </label>
                <input
                  type="text"
                  value={settings.music_label || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, music_label: e.target.value })
                  }
                  className="input"
                  placeholder="music"
                />
                <p className="text-dark-500 text-sm mt-1">
                  Label applied to torrents for easy filtering
                </p>
              </div>
            </div>
          </div>

          {/* Seedbox/Rsync Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Server size={20} />
              Seedbox Settings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Seedbox Host
                </label>
                <input
                  type="text"
                  value={settings.seedbox_host || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, seedbox_host: e.target.value })
                  }
                  className="input"
                  placeholder="user@seedbox.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  SSH Key Path
                </label>
                <input
                  type="text"
                  value={settings.seedbox_ssh_key_path || ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      seedbox_ssh_key_path: e.target.value,
                    })
                  }
                  className="input"
                  placeholder="/home/user/.ssh/id_rsa"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Seedbox Downloads Path
                </label>
                <input
                  type="text"
                  value={settings.seedbox_path || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, seedbox_path: e.target.value })
                  }
                  className="input"
                  placeholder="/home/user/downloads"
                />
              </div>
            </div>
          </div>

          {/* Local Storage Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <HardDrive size={20} />
              Local Storage Settings
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Local Music Path
                </label>
                <input
                  type="text"
                  value={settings.local_music_path || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, local_music_path: e.target.value })
                  }
                  className="input"
                  placeholder="/mnt/cephfs/music"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Naming Template
                </label>
                <input
                  type="text"
                  value={settings.naming_template || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, naming_template: e.target.value })
                  }
                  className="input"
                  placeholder="{artist}/{album}/{track_num} - {title}"
                />
                <p className="text-dark-500 text-sm mt-1">
                  Available placeholders: {'{artist}'}, {'{album}'},{' '}
                  {'{track_num}'}, {'{title}'}
                </p>
              </div>
            </div>
          </div>

          {/* Soulseek (slskd) Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Music size={20} className="text-cyan-400" />
              Soulseek (slskd) Settings
            </h2>
            <p className="text-dark-400 text-sm mb-4">
              Configure slskd to search Soulseek as a fallback when torrents aren't found.
              slskd is a Soulseek client with a REST API.{' '}
              <a
                href="https://github.com/slskd/slskd"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:underline"
              >
                Learn more
              </a>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  slskd URL
                </label>
                <input
                  type="url"
                  value={settings.slskd_url || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, slskd_url: e.target.value })
                  }
                  className="input"
                  placeholder="http://localhost:5030"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  value={settings.slskd_api_key || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, slskd_api_key: e.target.value })
                  }
                  className="input"
                  placeholder="API key from slskd settings"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.slskd_auto_fallback === 1}
                    onChange={(e) =>
                      setSettings({ ...settings, slskd_auto_fallback: e.target.checked ? 1 : 0 })
                    }
                    className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span className="text-sm text-dark-300">
                    Auto-search Soulseek when torrents not found
                  </span>
                </label>
                <p className="text-dark-500 text-sm mt-1 ml-8">
                  When enabled, albums marked "not found" after torrent search will automatically be searched on Soulseek
                </p>
              </div>
            </div>
          </div>

          {/* DAB (dabmusic.xyz) Settings */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Music size={20} className="text-purple-400" />
              DAB (High-Res Audio) Settings
            </h2>
            <p className="text-dark-400 text-sm mb-4">
              Configure DAB to download high-resolution audio (up to 24bit/192kHz FLAC).
              This is an alternative source for lossless music downloads.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={settings.dab_email || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, dab_email: e.target.value })
                  }
                  className="input"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={settings.dab_password || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, dab_password: e.target.value })
                  }
                  className="input"
                  placeholder="Your DAB password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Quality
                </label>
                <select
                  value={settings.dab_quality || 27}
                  onChange={(e) =>
                    setSettings({ ...settings, dab_quality: parseInt(e.target.value) })
                  }
                  className="input"
                >
                  <option value={5}>MP3 320kbps</option>
                  <option value={6}>FLAC 16bit/44.1kHz</option>
                  <option value={7}>FLAC 24bit/44.1kHz</option>
                  <option value={27}>FLAC 24bit Hi-Res (Best)</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.dab_enabled === 1}
                    onChange={(e) =>
                      setSettings({ ...settings, dab_enabled: e.target.checked ? 1 : 0 })
                    }
                    className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-purple-500 focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-sm text-dark-300">
                    Enable DAB as download source
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isAdmin && (
              <button
                type="submit"
                disabled={saveSettingsMutation.isPending}
                className="btn btn-primary flex items-center gap-2"
              >
                {saveSettingsMutation.isPending ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save Settings
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => testConnectionMutation.mutate()}
              disabled={testConnectionMutation.isPending}
              className="btn btn-secondary flex items-center gap-2"
            >
              {testConnectionMutation.isPending ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Settings size={18} />
                  Test Connections
                </>
              )}
            </button>
          </div>

          {/* Test results */}
          {testConnectionMutation.data && (
            <div className="card">
              <h3 className="text-lg font-semibold mb-3">Connection Status</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-dark-700 rounded">
                  <span>Prowlarr</span>
                  <span
                    className={`flex items-center gap-1 ${
                      testConnectionMutation.data.prowlarr?.success
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}
                  >
                    {testConnectionMutation.data.prowlarr?.success ? (
                      <>
                        <CheckCircle size={16} />
                        Connected
                      </>
                    ) : (
                      <>
                        <XCircle size={16} />
                        {testConnectionMutation.data.prowlarr?.message || 'Failed'}
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-dark-700 rounded">
                  <span>rTorrent</span>
                  <span
                    className={`flex items-center gap-1 ${
                      testConnectionMutation.data.rtorrent?.success
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}
                  >
                    {testConnectionMutation.data.rtorrent?.success ? (
                      <>
                        <CheckCircle size={16} />
                        Connected
                      </>
                    ) : (
                      <>
                        <XCircle size={16} />
                        {testConnectionMutation.data.rtorrent?.message || 'Failed'}
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-dark-700 rounded">
                  <span>Seedbox SSH</span>
                  <span
                    className={`flex items-center gap-1 ${
                      testConnectionMutation.data.rsync?.success
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}
                  >
                    {testConnectionMutation.data.rsync?.success ? (
                      <>
                        <CheckCircle size={16} />
                        Connected
                      </>
                    ) : (
                      <>
                        <XCircle size={16} />
                        {testConnectionMutation.data.rsync?.message || 'Failed'}
                      </>
                    )}
                  </span>
                </div>
              </div>
              {/* Test slskd separately */}
              {settings.slskd_url && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const result = await downloadSettings.testSlskd()
                      alert(result.success ? `Soulseek connected: ${result.message}` : `Soulseek failed: ${result.message}`)
                    } catch (err: any) {
                      alert(`Soulseek test failed: ${err?.response?.data || err.message}`)
                    }
                  }}
                  className="btn btn-secondary mt-3 flex items-center gap-2"
                >
                  <Music size={16} className="text-cyan-400" />
                  Test Soulseek Connection
                </button>
              )}
              {/* Test DAB separately */}
              {settings.dab_email && settings.dab_enabled === 1 && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const result = await downloadSettings.testDab()
                      alert(result.success ? `DAB connected: ${result.message}` : `DAB failed: ${result.message}`)
                    } catch (err: any) {
                      alert(`DAB test failed: ${err?.response?.data || err.message}`)
                    }
                  }}
                  className="btn btn-secondary mt-3 flex items-center gap-2"
                >
                  <Music size={16} className="text-purple-400" />
                  Test DAB Connection
                </button>
              )}
            </div>
          )}
        </form>
      )}

      {/* Sync Progress Modal */}
      {syncingJobId && jobs?.find(j => j.id === syncingJobId) && (() => {
        const job = jobs.find(j => j.id === syncingJobId)!
        const progress = getSyncProgress(job)
        if (!progress) return null

        return (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="card p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FolderSync size={20} className="text-green-400" />
                Syncing Albums
              </h3>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-dark-400 mb-1">
                  <span>{progress.current} / {progress.total}</span>
                  <span>{progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%</span>
                </div>
                <div className="h-3 bg-dark-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-300"
                    style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Current Album */}
              <div className="text-sm mb-4">
                <span className="text-dark-400">Current: </span>
                <span className="text-dark-200">{progress.currentAlbum}</span>
              </div>

              {/* Stats */}
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <CheckCircle size={14} className="text-green-400" />
                  <span className="text-dark-400">Synced:</span>
                  <span className="text-green-400">{progress.synced}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} className="text-blue-400" />
                  <span className="text-dark-400">Remaining:</span>
                  <span className="text-blue-400">{progress.remaining}</span>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Create Playlist Modal */}
      {showCreatePlaylistModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ListMusic size={20} className="text-purple-400" />
                Create Playlist
              </h2>
              <button
                onClick={() => {
                  setShowCreatePlaylistModal(null)
                  setNewPlaylistName('')
                }}
                className="p-1 hover:bg-dark-700 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Playlist Name
                </label>
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="input w-full"
                  placeholder="My Playlist"
                  autoFocus
                />
              </div>
              <div className="text-sm text-dark-400">
                <p className="flex items-center gap-2 mb-2">
                  <Info size={14} />
                  All tracks from this import will be added to the playlist:
                </p>
                <ul className="list-disc list-inside text-dark-500 space-y-1 ml-4">
                  <li>Tracks found in your library will be linked directly</li>
                  <li>Missing tracks will be added as "pending" for later</li>
                </ul>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={likeTracks}
                  onChange={(e) => setLikeTracks(e.target.checked)}
                  className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-sm text-dark-300">
                  Also add matched tracks to Liked Songs
                </span>
              </label>
            </div>
            <div className="p-4 border-t border-dark-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreatePlaylistModal(null)
                  setNewPlaylistName('')
                  setLikeTracks(false)
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showCreatePlaylistModal && newPlaylistName.trim()) {
                    createPlaylistMutation.mutate({
                      jobId: showCreatePlaylistModal,
                      playlistName: newPlaylistName.trim(),
                      likeTracks,
                    })
                  }
                }}
                disabled={createPlaylistMutation.isPending || !newPlaylistName.trim()}
                className="btn bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
              >
                {createPlaylistMutation.isPending ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <ListMusic size={16} />
                    Create Playlist
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
